/**
 * 问题详情页出错堆栈头部（DataHead）「异常名 (expName)」与「异常消息 (expMessage)」
 * 是否展示参与问题分类红色星号（<IconDefinitive/>）的判定工具。
 *
 * 对齐 process-classify 后端真实聚类规则（BaseMerger / LuaMerger / HandleMsgThread / MergeVersionUtils）：
 * 1. PC (isPcResult=true)：由 serverAppSettings.pcClassify.needExpName / needExpMessage 控制。
 * 2. 卡顿 Jank (type=140/141/145 或 exceptionCategory=JANK)：
 *    - HandleMsgThread:623 对 140/141 强制 setBanExpMessageClassify(true)，异常消息不参与分类；
 *    - BaseMerger:49 对 type=145 (ThreadMonitorJank) 将 errorType 置空，异常名与异常消息均不参与分类。
 * 3. 错误 Error (type=104/204/106/206/107/207/111~120/211~220 或 exceptionCategory=ERROR)：
 *    - LuaMerger:352 仅在 `mergeVersion > VERSION_10 || message.getUseExpMessageClassify()` 且 `!banExpMessageClassify` 时将 expMessage 纳入指纹；
 *    - MergeVersionUtils:53-79 中 iOS(pid=2)、Mac(pid=3) 最新 mergeVersion=6 (<=10)，微信小游戏(pid=32) mergeVersion=0 (<=10)，
 *      因此这三端下 Error 的异常消息默认不参与分类（不展示星号），仅当开启 CLASSIFY_EXPMESSAGE=ENABLE 白名单或命中 LuaMerger 消息聚类特例时展示；
 *    - Android(pid=1)、鸿蒙(pid=40)、主机(pid=20/21) 最新 mergeVersion=11 (>10)，默认参与分类（展示星号），但若配置 CLASSIFY_EXPMESSAGE=NOT_ENABLE 则不展示。
 * 4. 崩溃 / ANR / 卡死 (Crash / ANR / Hang)：
 *    - MetricKit (isMetricKit=true) 异常消息不参与分类；其余保持展示。
 */

const IOS_PID = 2;
const MAC_PID = 3;
const WECHAT_MINI_PROGRAM_PID = 32;

const LUA_TRACEBACK_PREFIX_APP_IDS = ['2e17210f2b', '0dca905c13', '294541631a'];
const GAMECORE_APP_IDS = ['8661e83bad', '65892365b9'];
const AUDIO_EXP_APP_IDS = ['1111186395', 'i1111186395', 'i1112201016', '1112201016'];
const LUA_DOT_STACK_APP_IDS = ['65892365b9', '2b3eb5847a', '6dd8864548', '8661e83bad', '41ea9b92f8', 'a536bc917e'];
const EMPTY_ERRORTYPE_APP_IDS = ['d93700ae09', '6d453ed847'];

function isErrorReport(exceptionCategory, reportType) {
  const cat = String(exceptionCategory || '').toUpperCase();
  if (cat === 'ERROR' || cat === 'ERRORS') {
    return true;
  }
  const t = String(reportType || '').trim();
  if (['104', '204', '106', '206', '107', '207'].includes(t)) {
    return true;
  }
  const num = Number(t);
  if (Number.isInteger(num) && ((num >= 111 && num <= 120) || (num >= 211 && num <= 220))) {
    return true;
  }
  return false;
}

function isJankReport(exceptionCategory, reportType) {
  const cat = String(exceptionCategory || '').toUpperCase();
  const t = String(reportType || '').trim();
  return cat === 'JANK' || cat === 'JANKS' || t === '140' || t === '141' || t === '145';
}

function isLuaMergerExpMessageSpecialHit({ appId, expName, expMessage, callStack }) {
  const app = String(appId || '');
  const name = String(expName || '');
  const msg = String(expMessage || '');
  const stack = String(callStack || '');
  if (!msg) {
    return false;
  }
  if (GAMECORE_APP_IDS.includes(app) && name === 'GameCore') {
    return true;
  }
  if (LUA_TRACEBACK_PREFIX_APP_IDS.includes(app) && msg.includes('stack traceback:')) {
    return true;
  }
  if (LUA_DOT_STACK_APP_IDS.includes(app) && msg.includes('.lua:')) {
    return true;
  }
  if (AUDIO_EXP_APP_IDS.includes(app) && name === 'Audio.Exception') {
    return true;
  }
  if (app === '46baac1df4' && name.includes('orderId')) {
    return true;
  }
  if (app === 'i1108169089' && msg.includes('LaunchVideoView')) {
    return true;
  }
  if (app === 'i1107922114' && ((name === 'LuaException' && stack.includes('XLua')) || stack.includes('MS.Framework.Network.NetworkTransceiver.Receive(Byte[], Int32, UInt64)'))) {
    return true;
  }
  if (
    app === '25ff8e8127'
    && (
      stack.includes('UnityEngine.Application.CallLogCallback')
      || stack.includes('XLua.CSObjectWrap.CNCCommonLuaCallWrap._m_StaticLogError_xlua_st_(IntPtr)')
      || stack.includes('XLua.LuaEnv.ThrowExceptionFromError')
      || stack.includes('XLua.CSObjectWrap.UnityEngineGameObjectWrap')
    )
  ) {
    return true;
  }
  if (name.includes('TeammateDisappearException')) {
    return true;
  }
  return false;
}

/**
 * 判断「异常名 (expName)」是否展示参与问题分类星号
 */
export function shouldShowExpNameClassifyMark({
  isPcResult = false,
  needExpName = false,
  reportType = '',
  appId = '',
} = {}) {
  if (isPcResult) {
    return Boolean(needExpName);
  }
  if (String(reportType || '').trim() === '145') {
    return false;
  }
  if (EMPTY_ERRORTYPE_APP_IDS.includes(String(appId || ''))) {
    return false;
  }
  return true;
}

/**
 * 判断「异常消息 (expMessage)」是否展示参与问题分类星号
 */
export function shouldShowExpMessageClassifyMark({
  pid,
  isPcResult = false,
  needExpMessage = false,
  isMetricKit = false,
  exceptionCategory = '',
  reportType = '',
  appId = '',
  expName = '',
  expMessage = '',
  callStack = '',
  classifyExpMessageEnable = undefined,
} = {}) {
  if (isMetricKit) {
    return false;
  }
  if (isPcResult) {
    return Boolean(needExpMessage);
  }
  if (isJankReport(exceptionCategory, reportType)) {
    return false;
  }
  if (isErrorReport(exceptionCategory, reportType)) {
    if (typeof classifyExpMessageEnable === 'boolean') {
      return classifyExpMessageEnable;
    }
    if (isLuaMergerExpMessageSpecialHit({ appId, expName, expMessage, callStack })) {
      return true;
    }
    const numPid = Number(pid);
    if (numPid === IOS_PID || numPid === MAC_PID || numPid === WECHAT_MINI_PROGRAM_PID) {
      return false;
    }
    return true;
  }
  return true;
}
